import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {StepTransitionStatusType} from 'notebook/worksheets/steps/WorksheetStepButton';
import {Worksheet, WorksheetTaskStatusType} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetViewQuery} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import {TransitionStatusType} from 'procedures/worksheets/TransitionRunModal';
import {extractProcedureRunDataFromTask} from 'procedures/worksheets/WorksheetUtils';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {EventMetadata, NOT_SPECIFIED, ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_WORKSHEET_EVENTS_VERSION = 5;

export enum ProfilerWorksheetsFeatures {
  WORKSHEET_TRANSITION_RUN_STATUS = 'worksheetTransitionRunStatus',
  WORKSHEET_STEP_TRANSITION_STATUS = 'worksheetStepTransitionStatus',
  WORKSHEET_INITIAL_LOAD = 'worksheetInitialLoad',
  WORKSHEET_STEP_COMPLETION = 'worksheetStepCompletion',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerWorksheetsFeatures]: Function} = {
  [ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS]: processWorksheetTransitionRunStatus,
  [ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS]: processWorksheetStepTransitionStatus,
  [ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD]: processWorksheetInitialLoad,
  [ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION]: processWorksheetStepCompletion,
};

enum ProfilerWorksheetActions {
  WORKSHEET_TRANSITION_RUN_STATUS_STARTED = 'worksheetTransitionRunStatusStarted',
  WORKSHEET_TRANSITION_RUN_STATUS_ENDED = 'worksheetTransitionRunStatusEnded',
  WORKSHEET_STEP_TRANSITION_STATUS_STARTED = 'worksheetStepTransitionStatusStarted',
  WORKSHEET_STEP_TRANSITION_STATUS_ENDED = 'worksheetStepTransitionStatusEnded',
  WORKSHEET_INITIAL_LOAD_STARTED = 'worksheetInitialLoadStarted',
  WORKSHEET_INITIAL_LOAD_ENDED = 'worksheetIntialLoadEnded',
  WORKSHEET_STEP_COMPLETION_STARTED = 'worksheetStepCompletionStarted',
  WORKSHEET_STEP_COMPLETION_ENDED = 'worksheetStepCompletionEnded',
}

export const WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_TRANSITION_RUN_STATUS_STARTED,
};
export const WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_TRANSITION_RUN_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_TRANSITION_RUN_STATUS_ENDED,
};

export const WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_TRANSITION_STATUS_STARTED,
};
export const WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_TRANSITION_STATUS,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_TRANSITION_STATUS_ENDED,
};

export const WORKSHEET_INITIAL_LOAD_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD,
  action: ProfilerWorksheetActions.WORKSHEET_INITIAL_LOAD_STARTED,
};
export const WORKSHEET_INITIAL_LOAD_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_INITIAL_LOAD,
  action: ProfilerWorksheetActions.WORKSHEET_INITIAL_LOAD_ENDED,
};

export const WORKSHEET_STEP_COMPLETION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_COMPLETION_STARTED,
};
export const WORKSHEET_STEP_COMPLETION_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.WORKSHEETS,
  feature: ProfilerWorksheetsFeatures.WORKSHEET_STEP_COMPLETION,
  action: ProfilerWorksheetActions.WORKSHEET_STEP_COMPLETION_ENDED,
};

/**
 * Mapping from a worksheet run transition status type to action name.
 * Uses a Record to ensure any changes to the TransitionStatusType enum are reflected here.
 */
const transitionRunStatusTypeToActionName: Record<TransitionStatusType, string> = {
  [WorksheetTaskStatusType.IN_PROGRESS]: 'start_run',
  [WorksheetTaskStatusType.COMPLETED]: 'complete_run',
  [WorksheetTaskStatusType.FAILED]: 'fail_run',
  [WorksheetTaskStatusType.SKIPPED]: 'skip_run',
};
/**
 * Mapping from a worksheet step transition status type to action name.
 * Uses a Record to ensure any changes to the TransitionStatusType enum are reflected here.
 */
const transitionStepStatusTypeToActionName: Record<StepTransitionStatusType, string> = {
  [WorksheetTaskStatusType.READY]: 'amend_step',
  [WorksheetTaskStatusType.COMPLETED]: 'complete_step',
  [WorksheetTaskStatusType.FAILED]: 'fail_step',
  [WorksheetTaskStatusType.SKIPPED]: 'skip_step',
};

/**
 * Mapping from a procedure type to the type name in the dashboard.
 * Uses a Record to ensure any changes to the ProcedureType enum are reflected here.
 */
export const procedureTypeToName: Record<ProcedureType, string> = {
  [ProcedureType.ASSAY]: 'assay',
  [ProcedureType.RECIPE]: 'recipe',
};

/**
 * Build metadata for worksheet transition run status event
 */
export function composeWorksheetTransitionRunEventMetadata(
  transitionToStatus: TransitionStatusType,
  transitionFromStatus?: WorksheetTaskStatusType,
  worksheet?: Worksheet | null
): EventMetadata {
  const {procedureType} = extractProcedureRunDataFromTask(worksheet?.tasks[0]);
  return {
    distributionMetadata: {
      action: transitionRunStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
    },
    actionMetadata: {
      transition_from_status: toDatadogTagFormat(transitionFromStatus ?? NOT_SPECIFIED),
      transition_to_status: toDatadogTagFormat(transitionToStatus),
      action: transitionRunStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
    },
  };
}

/**
 * Build metadata for worksheet step transition status event
 */
export function composeWorksheetStepTransitionEventMetadata(
  transitionToStatus: StepTransitionStatusType,
  transitionFromStatus?: WorksheetTaskStatusType,
  worksheet?: Worksheet | null
): EventMetadata {
  const {procedureType} = extractProcedureRunDataFromTask(worksheet?.tasks[0]);
  return {
    distributionMetadata: {
      action: transitionStepStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
    },
    actionMetadata: {
      transition_from_status: toDatadogTagFormat(transitionFromStatus ?? NOT_SPECIFIED),
      transition_to_status: toDatadogTagFormat(transitionToStatus),
      action: transitionStepStatusTypeToActionName[transitionToStatus],
      worksheet_type: procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED,
    },
  };
}

/**
 * Build metadata for worksheet initial load event
 */
export function composeWorksheetInitialLoadEventMetadata(
  worksheet: WorksheetViewQuery['worksheet']
): EventMetadata {
  let worksheetType = NOT_SPECIFIED;
  let stepCount = 0;
  if (!isInaccessible(worksheet)) {
    const {procedureType} = extractProcedureRunDataFromTask(worksheet?.tasks[0]);
    worksheetType = procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED;
    stepCount = worksheet?.worksheetStepGroups.flatMap((stepGroup) => stepGroup.worksheetSteps).length ?? 0;
  }

  return {
    distributionMetadata: {
      worksheet_type: worksheetType,
    },
    actionMetadata: {
      worksheet_type: worksheetType,
      step_count: stepCount,
    },
  };
}

/**
 * Build metadata for worksheet step completion event
 */
export function composeWorksheetStepCompletionEventMetadata(worksheet?: Worksheet | null): EventMetadata {
  const {procedureType} = extractProcedureRunDataFromTask(worksheet?.tasks[0]);
  const worksheetType = procedureType ? procedureTypeToName[procedureType] : NOT_SPECIFIED;

  return {
    distributionMetadata: {
      worksheet_type: worksheetType,
    },
    actionMetadata: {
      worksheet_type: worksheetType,
    },
  };
}

export function processWorksheetsEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerWorksheetsFeatures;
  assert(
    Object.values(ProfilerWorksheetsFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Process a Worksheet Transition Run Status event
 */
function processWorksheetTransitionRunStatus(): void {
  processTimingEvent(
    WORKSHEET_TRANSITION_RUN_STATUS_STARTED_EVENT,
    WORKSHEET_TRANSITION_RUN_STATUS_ENDED_EVENT,
    'Worksheet Transition Run Status',
    'perf.worksheet_transition_run_status',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Step Transition Status event
 */
function processWorksheetStepTransitionStatus(): void {
  processTimingEvent(
    WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT,
    WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT,
    'Worksheet Step Transition Status',
    'perf.worksheet_step_transition_status',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Initial Load event
 */
function processWorksheetInitialLoad(): void {
  processTimingEvent(
    WORKSHEET_INITIAL_LOAD_STARTED_EVENT,
    WORKSHEET_INITIAL_LOAD_ENDED_EVENT,
    'Worksheet Initial Load',
    'perf.worksheet_initial_load',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}

/**
 * Process a Worksheet Step Completion event
 */
function processWorksheetStepCompletion(): void {
  processTimingEvent(
    WORKSHEET_STEP_COMPLETION_STARTED_EVENT,
    WORKSHEET_STEP_COMPLETION_ENDED_EVENT,
    'Worksheet Step Completion',
    'perf.worksheet_step_completion',
    PROFILER_WORKSHEET_EVENTS_VERSION
  );
}
